/**
 * parentRecap.ts — pure renderer for the parent-facing "What your kid
 * did today" plain-language recap.
 *
 * Per the project rules (effectiveslp-ios rule #5 — plain language,
 * no clinical jargon), the recap is rendered from the same rollup
 * data the clinician view consumes, but strip words like "data
 * collection", "IEP", "bail trial index", "cue usage rate" out of
 * the surface language.
 *
 * Pure function: no React, no Supabase, no DOM. Unit-testable with
 * fixture rollups.
 */

import type { RollupResponse } from './parentTypes';

export interface ParentRecap {
  /** "Today at 4:32 PM" / "Yesterday at 5:11 PM" / "Jun 30 at 4:32 PM". */
  whenLabel: string;
  /** Total session length in minutes (rounded). */
  durationMinutes: number;
  /** How many activities the kid attempted. */
  activitiesAttempted: number;
  /** How many of those activities were completed in full. */
  activitiesCompleted: number;
  /** Total trials attempted. */
  trialsAttempted: number;
  /** Total trials correct. */
  trialsCorrect: number;
  /** Accuracy percentage (0-100), rounded. */
  accuracyPct: number;
  /** Plain-language summary line. */
  summary: string;
  /** True if there was no practice data within the last 7 days. */
  isStale: boolean;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return `Today at ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return `Yesterday at ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
  }
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} at ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

function isWithinLastWeek(iso: string): boolean {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return false;
  return Date.now() - ms <= 7 * 24 * 60 * 60 * 1000;
}

function pickLatestSession(rollup: RollupResponse): RollupResponse['sessions'][number] | null {
  const sessions = rollup.sessions;
  if (sessions.length === 0) return null;
  let best: RollupResponse['sessions'][number] = sessions[0]!;
  for (const s of sessions) {
    if (s.started_at > best.started_at) best = s;
  }
  return best;
}

export function buildParentRecap(rollup: RollupResponse | null): ParentRecap | null {
  if (!rollup) return null;
  const session = pickLatestSession(rollup);
  if (!session) return null;

  const itemsAttempted = rollup.per_item_rollup.filter(
    (it) => it.trials_attempted > 0,
  );
  const trialsAttempted = session.trials_attempted ?? itemsAttempted.reduce(
    (sum, it) => sum + it.trials_attempted,
    0,
  );
  const trialsCorrect = session.trials_correct ?? itemsAttempted.reduce(
    (sum, it) => sum + it.trials_correct,
    0,
  );
  const accuracyPct = trialsAttempted > 0
    ? Math.round((trialsCorrect / trialsAttempted) * 100)
    : 0;

  const durationMinutes = session.duration_seconds
    ? Math.max(1, Math.round(session.duration_seconds / 60))
    : 0;

  const phrases: string[] = [];
  if (itemsAttempted.length > 0) {
    if (itemsAttempted.length === 1) {
      phrases.push('tried 1 activity');
    } else {
      phrases.push(`tried ${itemsAttempted.length} activities`);
    }
  }
  if (trialsAttempted > 0) {
    if (trialsAttempted === 1) {
      phrases.push('answered 1 question');
    } else {
      phrases.push(`answered ${trialsAttempted} questions`);
    }
  }
  if (accuracyPct >= 0 && trialsAttempted > 0) {
    if (accuracyPct >= 80) {
      phrases.push(`got ${accuracyPct}% right`);
    } else if (accuracyPct >= 50) {
      phrases.push(`got ${accuracyPct}% right`);
    } else {
      phrases.push(`got ${accuracyPct}% right — keep practicing`);
    }
  }

  const summary = phrases.length > 0
    ? `Your kid ${phrases.join(', ')}.`
    : 'Your kid did a little practice.';

  return {
    whenLabel: formatWhen(session.started_at),
    durationMinutes,
    activitiesAttempted: itemsAttempted.length,
    activitiesCompleted: session.activities_completed ?? 0,
    trialsAttempted,
    trialsCorrect,
    accuracyPct,
    summary,
    isStale: !isWithinLastWeek(session.started_at),
  };
}

/**
 * Bucket sessions into "this week" vs "last week" for the dashboard
 * comparison strip.
 */
export interface WeekComparison {
  thisWeek: { sessions: number; trialsAttempted: number; trialsCorrect: number; accuracyPct: number };
  lastWeek: { sessions: number; trialsAttempted: number; trialsCorrect: number; accuracyPct: number };
  /** True if this week's accuracy is strictly greater than last week's. */
  trending: 'up' | 'down' | 'flat' | 'insufficient';
}

function weekStartUtc(d: Date): number {
  const day = d.getUTCDay();
  const offset = day === 0 ? 6 : day - 1;
  const monday = new Date(d);
  monday.setUTCDate(monday.getUTCDate() - offset);
  monday.setUTCHours(0, 0, 0, 0);
  return monday.getTime();
}

export function buildWeekComparison(rollup: RollupResponse | null): WeekComparison {
  const empty = {
    sessions: 0,
    trialsAttempted: 0,
    trialsCorrect: 0,
    accuracyPct: 0,
  };
  if (!rollup) {
    return { thisWeek: empty, lastWeek: empty, trending: 'insufficient' };
  }
  const now = new Date();
  const thisStart = weekStartUtc(now);
  const lastStart = thisStart - 7 * 24 * 60 * 60 * 1000;

  const thisWeekSessions: RollupResponse['sessions'] = [];
  const lastWeekSessions: RollupResponse['sessions'] = [];
  for (const s of rollup.sessions) {
    const t = Date.parse(s.started_at);
    if (Number.isNaN(t)) continue;
    if (t >= thisStart) {
      thisWeekSessions.push(s);
    } else if (t >= lastStart && t < thisStart) {
      lastWeekSessions.push(s);
    }
  }

  function summarize(arr: RollupResponse['sessions']) {
    const sessions = arr.length;
    const trialsAttempted = arr.reduce(
      (sum, s) => sum + (s.trials_attempted ?? 0),
      0,
    );
    const trialsCorrect = arr.reduce(
      (sum, s) => sum + (s.trials_correct ?? 0),
      0,
    );
    const accuracyPct = trialsAttempted > 0
      ? Math.round((trialsCorrect / trialsAttempted) * 100)
      : 0;
    return { sessions, trialsAttempted, trialsCorrect, accuracyPct };
  }

  const thisWeek = summarize(thisWeekSessions);
  const lastWeek = summarize(lastWeekSessions);

  if (thisWeek.sessions === 0 && lastWeek.sessions === 0) {
    return { thisWeek, lastWeek, trending: 'insufficient' };
  }

  let trending: WeekComparison['trending'] = 'flat';
  if (thisWeek.accuracyPct > lastWeek.accuracyPct) {
    trending = 'up';
  } else if (thisWeek.accuracyPct < lastWeek.accuracyPct) {
    trending = 'down';
  }

  return { thisWeek, lastWeek, trending };
}
